import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ProgramRequestDetail from 'components/global/ProgramRequestDetail';
import { fetchProgramRequest } from 'components/global/ProgramRequestDetail/ProgramRequestDetail.helpers';
import { checkEdoProgramPermissions } from '../../../../libs/edoPermissions';
import applicationDetailSignal from 'components/global/ApplicationDetail/ApplicationDetail.signal';
import loaderSignal from '../../../../signals/Loader.signal';
import BreadCrumbWrapper from '../../../global/BreadCrumbs/BreadCrumbs';
import userAccountSignal from '../../../../signals/UserAccount.signal';

const EdoProgramRequestDetail = () => {
  const { pathname } = useLocation();
  const programRequestId = pathname.split('/')[pathname.split('/').length - 1];
  const { fundingProgram, id: loadedProgramRequestId } =
    applicationDetailSignal.value;
  const isCurrentProgramRequestLoaded =
    String(loadedProgramRequestId) === String(programRequestId);
  const [canView, setCanView] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isLoadingProgramRequest, setIsLoadingProgramRequest] = useState(true);

  // Fetch program request first so applicationDetailSignal (including fundingProgram) is populated
  useEffect(() => {
    if (!programRequestId) {
      setIsLoadingProgramRequest(false);
      return;
    }
    let cancelled = false;
    fetchProgramRequest(programRequestId)
      .then(() => {
        if (!cancelled) setIsLoadingProgramRequest(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoadingProgramRequest(false);
      });
    return () => {
      cancelled = true;
    };
  }, [programRequestId]);

  // Check permissions when program request for this URL is loaded
  useEffect(() => {
    const checkPermissions = async () => {
      if (!fundingProgram?.id || !isCurrentProgramRequestLoaded) {
        return;
      }

      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const permissions = await checkEdoProgramPermissions(
          fundingProgram.id,
          ['canView', 'canUpdate']
        );
        setCanView(permissions.canView);
        setCanUpdate(permissions.canUpdate);
        setIsChecking(false);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanUpdate(false);
        setIsChecking(false);
      } finally {
        loaderSignal.reset();
      }
    };

    checkPermissions();
  }, [
    fundingProgram?.id,
    isCurrentProgramRequestLoaded,
    userAccountSignal.value.userData,
  ]);

  const isLoading =
    isLoadingProgramRequest || (isCurrentProgramRequestLoaded && isChecking);
  if (isLoading) {
    return (
      <BreadCrumbWrapper labels={['']}>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <p className="text-center">Loading...</p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  if (!isChecking && !canView) {
    return (
      <BreadCrumbWrapper labels={['']}>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <h3 className="text-center">Access Denied</h3>
          <p className="text-center">
            You do not have permission to view this program request.
          </p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  return <ProgramRequestDetail canUpdate={canUpdate} />;
};

export default EdoProgramRequestDetail;
